<template>

  <section class="bg-gray-200 relative">
    <div class="container">
      <div class="pb-[35%]"></div>
    </div>
    <img
      class="lazy object-cover absolute inset-0 w-full h-full max-w-[1920px] mx-auto"
      data-src="/media/gift-cards.png"
      height="1866"
      width="2800"
      alt="Gutscheine"
    >
  </section>

  <section class="py-20">
    <div class="text-center pb-20">
      <h2 class="text-lg sm:text-2xl">Kleine Karte für viel Theater</h2>
      <div class="text-3xl sm:text-4xl text-blue-500 font-bold">Theaterliebe zum Verschenken</div>
      <div class="container max-w-md md:max-w-4xl mt-12">
        <div class="grid md:grid-cols-2 gap-10 items-start justify-center">
          <template v-for="(item, key) in items" :key="item.id">
            <Card
              theme="blue"
              :title="item.name"
              :image="item.cover"
              :link="route('vouchers.show', [item])"
              :alt="item.name"
            >
              <template #caption>
                {{ item.short_desc }}
              </template>
              <template #footer>
                <strong class="text-xl text-blue-500 mb-4 sm:mb-0">
                  {{ price(item) }}
                </strong>
                <Link
                  :href="route('vouchers.show', [item])"
                  class="button button--ghost text-blue-500 ml-auto w-full sm:w-auto"
                  text="Mehr erfahren"
                />
              </template>
            </Card>
          </template>
        </div>
      </div>
    </div>
  </section>

  <section class="bg-gradient-to-r from-blue-500 to-blue-accent text-white py-12">
    <div class="container text-center">
      <div class="text-lg sm:text-2xl">Mein DAS DA-Gutschein</div>
      <h2 class="text-3xl sm:text-4xl font-bold">Guthabenwert</h2>
    </div>
  </section>

  <section class="py-20">
    <div class="container text-center">
      <div class="sm:text-lg">
        <template v-if="result?.expires_at">
          <p>
            <template v-if="result.card">
              Das Guthaben für die DAS DA-Card beträgt:
            </template>
            <template v-else>
              Das Guthaben für den Gutschein beträgt:
            </template>
          </p>
          <strong class="font-bold text-5xl text-blue-500">
            {{ formatPrice(result.remaining) }}
          </strong>
          <p class="text-sm text-gray-700">
            Gültig bis zum {{ formatDate(result.expires_at, 'DD.MM.YYYY') }}
          </p>
        </template>
        <template v-else>
          <p>
            Finden Sie heraus, wie viel Guthaben sich auf Ihrem DAS DA-Gutschein befindet.<br>
            Tragen Sie hier Ihren Code ein:
          </p>
        </template>
      </div>

      <form
        class="mt-12 max-w-md mx-auto"
        :action="route('vouchers.credit')"
        @submit.prevent="submit"
      >
        <TextField
          v-model="form.code"
          autocomplete="off"
          placeholder="Code eingeben"
          class="text-center"
          :error="result?.error"
        />
        <button
          class="button mt-4"
          type="submit"
          :class="{ 'bg-gray-400': !form.code }"
          :disabled="!form.code"
        >
          Guthaben abfragen
        </button>
      </form>
    </div>
  </section>

  <section class="py-20">
    <div class="container">

      <div class="text-center pb-20">
        <h2 class="text-lg sm:text-2xl">Häufig gestellte Fragen</h2>
        <div class="text-3xl sm:text-4xl text-blue-500 font-bold">FAQ</div>
      </div>

      <div class="mx-auto max-w-md md:max-w-full grid md:grid-cols-2 gap-y-16 gap-x-12">

        <div class="text">
          <h3 class="text-blue-500">arum kann ich meinen Gutschein-Code nicht im Onlineshop einlösen?</h3>
          <p>ollten Sie einen DAS DA-Gutschein besitzen, der vor August 2021 oder sogar handschriftlichen ausgestellt wurde, ist dieser leider nicht mehr gültig. Sie besitzen bereits einen digitalen Code und haben dennoch Probleme bei Ihrer Online-Bestellung? Bitte wenden Sie sich direkt an unser Support-Team unter <a href="mailto:theater@dasda.de">theater@dasda.de</a>.</p>
        </div>

        <div class="text">
          <h3 class="text-blue-500">Wie erhalte ich meinen DAS DA-Gutschein?</h3>
          <p>estellen Sie einen DAS DA-Gutschein über unseren Onlineshop, schicken wir Ihnen Ihren Gutschein-Code als PDF-Datei per E-Mail zu. Diesen Code benötigen Sie, um Ihren Guthabenwert bei einer späteren Bestellung einlösen zu können. Für eine haptische Gutscheinkarte wählen Sie bitte bei Versandart „Postversand“ aus. Sobald Ihre Zahlung bei uns eingegangen ist, erhalten Sie dann zusätzlich zu dem digitalen Code Ihre Bestellung per Post.</p>
        </div>

        <div class="text">
          <h3 class="text-blue-500">Können Gutscheine mehrfach benutzt werden?</h3>
          <p>Entspricht ein Bestellwert nicht dem vollständigen Guthaben des Gutscheins bleibt automatisch ein Restwert erhalten. Weiter oben auf dieser Seite können Sie prüfen, wie viel Guthaben noch vorhanden ist. Reicht das Guthaben nicht für eine weitere Bestellung, bezahlen Sie lediglich den Differenzbetrag.</p>
        </div>

        <div class="text">
          <h3 class="text-blue-500">Welchen Wert haben die Gutscheine?</h3>
          <p>Von kleiner bis großer Theaterfreude: Sie entscheiden, was Sie anderen oder sich selbst schenken möchten. DAS DA-Gutscheine sind im <a :href="route('vouchers.index')">Onlineshop</a> in den festgelegten Warenwerten von 25 €, 50 € und 100 € erhältlich. Gutscheine über einen individuellen Wunschbetrag erhalten Sie in unseren Vorverkaufsstellen.</p>
        </div>

        <div class="text">
          <h3 class="text-blue-500">Wie lange ist mein Gutschein gültig?</h3>
          <p>Ein DAS DA-Gutschein ist ab dem Ausstellungsdatum 3 Jahre gültig. Dieses Datum ist bei Ihrem jeweiligen Gutscheincode mit angeben. Das Ablaufdatum können Sie jederzeit genau wie Ihr Guthaben oben auf dieser Seite abrufen. Bitte haben Sie dafür Verständnis, dass Gutscheine nach Ablauf dieser 3-Jahres-Frist nicht mehr eingelöst werden können.</p>
        </div>

      </div>

    </div>
  </section>
</template>

<script>
import Layout from 'Web/Layouts/WithCart.vue';
import TextField from 'Web/Form/Fields/TextField.vue';
import Card from 'Web/Shared/Card.vue';
import Link from 'Web/Shared/Link.vue';
import useMeta from '@/compose/meta';
import { useForm } from '@inertiajs/inertia-vue3';
import formatPrice from '@/helpers/formatPrice';
import formatDate from '@/helpers/formatDate';

export default {
  inheritAttrs: false,
  layout: Layout,
  components: {
    TextField,
    Card,
    Link,
  },
  props: {
    code: {
      type: String,
    },
    result: {
      type: Object,
    },
    items: {
      type: Array,
      default: () => [],
    }
  },
  setup(props) {
    const { assign } = useMeta();

    assign({ title: 'Guthaben' });

    const form = useForm({
      code: props.code,
    });

    function submit(event) {
      form.get(event.target.action, {
        preserveScroll: true,
        replace: true,
      });
    }

    function price(item) {
      let priceRange = item.options && item.options.length;
      let price;

      if (priceRange) {
        price = Math.min(...item.options.map(option => option.price));
        return 'ab ' + formatPrice(price);
      }

      return formatPrice(price);
    }

    return {
      formatDate,
      formatPrice,
      form,
      price,
      submit,
    }
  }
}
</script>
